import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useRoute } from "wouter";
import {
  OfferAcceptedScreen,
  OfferDeclinedScreen,
  OfferLoadingScreen,
} from "@/components/offer-status-screens";
import type { InvestmentRound } from "@/components/paymix-calculator";
import { PaymixCalculator } from "@/components/paymix-calculator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { handleOfferMutationError } from "@/lib/offer-verification";
import { rpcClient } from "@/lib/rpc";

interface OfferSessionData {
  id: string;
  salaryBase: string;
  equityShares: number | null;
  signingBonus: string | null;
  performanceBonus: string | null;
  performanceBonusShares: number | null;
  performanceBonusSharesMax: number | null;
  status: string;
  verificationToken?: string;
  allowPaymixAdjustment: boolean;
  paymixVariationPct: number | null;
  investmentRounds: InvestmentRound[];
  strikePrice: number;
  fxRate: string | null;
  application?: {
    applicant?: {
      fullName: string;
      email: string;
    };
    jobOpening?: {
      title: string;
      department: string;
      jobLevel: string;
    };
  };
}

export default function OfferAcceptPaymix() {
  const [, params] = useRoute("/accept-offer/:token/paymix");
  const token = params?.token;

  const [offer, setOffer] = useState<OfferSessionData | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [step, setStep] = useState<"loading" | "view" | "accepted" | "declined" | "error">(
    "loading",
  );
  const [acceptedSalaryBase, setAcceptedSalaryBase] = useState<number>(0);
  const [acceptedEquityShares, setAcceptedEquityShares] = useState<number>(0);
  const { toast } = useToast();

  useEffect(() => {
    if (!token) return;

    rpcClient.offers
      .publicOfferSession({ token })
      .then((data) => {
        const sessionData = data as unknown as OfferSessionData;
        setOffer(sessionData);
        setVerificationToken(sessionData.verificationToken ?? null);
        const base = parseFloat(sessionData.salaryBase);
        setAcceptedSalaryBase(base);
        setAcceptedEquityShares(sessionData.equityShares ?? 0);
        setStep("view");
      })
      .catch(() => {
        // No session — redirect to offer view for OTP verification
        window.location.href = `/offer/${token}`;
      });
  }, [token]);

  const handleValuesChange = useCallback(
    ({ salary, shares }: { salary: number; shares: number }) => {
      setAcceptedSalaryBase(salary);
      setAcceptedEquityShares(shares);
    },
    [],
  );

  const acceptOffer = useMutation({
    mutationFn: async () => {
      return rpcClient.offers.publicAcceptOffer({
        token: token!,
        verificationToken: verificationToken!,
        acceptedSalaryBase,
        acceptedEquityShares,
      });
    },
    onSuccess: () => {
      setStep("accepted");
    },
    onError: (error: Error) => {
      handleOfferMutationError(error, token, () => {
        toast({
          title: "Erro",
          description: error.message || "Não foi possível aceitar a oferta.",
          variant: "destructive",
        });
      });
    },
  });

  const declineOffer = useMutation({
    mutationFn: async () => {
      return rpcClient.offers.publicDeclineOffer({
        token: token!,
        verificationToken: verificationToken!,
      });
    },
    onSuccess: () => {
      setStep("declined");
    },
    onError: (error: Error) => {
      handleOfferMutationError(error, token, () => {
        toast({
          title: "Erro",
          description: error.message || "Não foi possível recusar a oferta.",
          variant: "destructive",
        });
      });
    },
  });

  if (step === "loading") {
    return <OfferLoadingScreen />;
  }

  if (step === "error" || !offer) {
    return null;
  }

  if (step === "accepted") {
    return <OfferAcceptedScreen />;
  }

  if (step === "declined") {
    return <OfferDeclinedScreen />;
  }

  const applicant = offer.application?.applicant;
  const fxRate = offer.fxRate ? parseFloat(offer.fxRate) : 1;
  const showSlider = offer.allowPaymixAdjustment && (offer.equityShares ?? 0) > 0;

  return (
    <div className="h-full overflow-y-auto bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center pb-2">
            {showSlider ? (
              <>
                <CardTitle className="text-2xl">Personalize sua compensação</CardTitle>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed max-w-lg mx-auto">
                  {applicant?.fullName ? `${applicant.fullName}, a` : "A"} Comp acredita que cada
                  pessoa tem preferências diferentes sobre como receber sua compensação. Use o
                  controle abaixo para ajustar o equilíbrio entre salário e equity dentro do range
                  aprovado. O valor total da sua compensação permanece o mesmo — apenas a
                  distribuição muda.
                </p>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl">Sua proposta de compensação</CardTitle>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed max-w-lg mx-auto">
                  {applicant?.fullName ? `${applicant.fullName}, c` : "C"}onfira abaixo os detalhes
                  completos da sua proposta.
                </p>
              </>
            )}
          </CardHeader>
          <CardContent>
            <PaymixCalculator
              baseSalary={parseFloat(offer.salaryBase)}
              shares={offer.equityShares ?? 0}
              fxRate={fxRate}
              signingBonus={offer.signingBonus ? parseFloat(offer.signingBonus) : undefined}
              performanceBonus={
                offer.performanceBonus ? parseFloat(offer.performanceBonus) : undefined
              }
              performanceBonusShares={offer.performanceBonusShares ?? 0}
              performanceBonusSharesMax={offer.performanceBonusSharesMax ?? undefined}
              allowSlider={showSlider}
              variationPct={offer.paymixVariationPct ?? undefined}
              rounds={offer.investmentRounds}
              strikePrice={offer.strikePrice}
              onValuesChange={handleValuesChange}
            />
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => declineOffer.mutate()}
            disabled={declineOffer.isPending || !verificationToken}
            data-testid="button-decline-offer"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {declineOffer.isPending ? "Processando..." : "Recusar"}
          </Button>
          <Button
            className="flex-1"
            onClick={() => acceptOffer.mutate()}
            disabled={acceptOffer.isPending || !verificationToken}
            data-testid="button-accept-offer"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {acceptOffer.isPending ? "Processando..." : "Aceitar Proposta"}
          </Button>
        </div>
      </div>
    </div>
  );
}
